import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRestaurant } from '../../../contexts/restaurant.context';
import classNames from 'classnames';
import { getWorkHours } from '../../../components/common';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useCategoryGroup } from '../../../contexts/category-group.context';
import { FullScreenOverlay } from '../../../components/FullScreenOverlay';
import { showCategoryDisabledAlert, showMenuItem } from '../../../store/actions/menu.actions';
import { cartResetSpecialCategory, cartSetSpecialCategory } from '../../../store/actions/cart.actions';
import { showCartCategoryMixAlert } from '../../../store/actions/checkout.actions';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../components/Modal';

export const CategoryListAndSearchItemButton = ({ defaultOpen = false }) => {
    const dispatch = useDispatch();
    
    const restaurant = useRestaurant();
    
    const cart = useSelector(state => state.cart.items);
    const specialCategory = useSelector(state => state.cart.specialCategory);
    
    const [categoryGroup, setCategoryGroup] = useCategoryGroup();

    const categoriesDropdownRef = useRef(null);
    const searchItemsRef = useRef(null);
    
    const [open, setOpen] = useState(defaultOpen);
    const [openSearch, setOpenSearch] = useState(false);
    const [scrollIntoView, setScrollIntoView] = useState(null);
    const [search, setSearch] = useState('');
    const [error, setError] = useState(null);
    
    const categories = useMemo(() => (restaurant?.menu_categories ?? []).filter((category) => {
        if (category.items.length === 0) { return false; }
        if ('hide' === category.display_category_schedule && (category.is_special_category || category.is_forced_schedule)) {
            const currentWorkHours = getWorkHours({...restaurant, business_hours: category.business_hours});
            if (!!!currentWorkHours) { return false; }
        }

        return true;
    }), [restaurant]);
    
    const foundItems = useMemo(() => {
        if (search.length === 0) {
            return [];
        }

        const term = search.toLowerCase();
        
        return categories
            .map((category) => category.items.filter((item) => item.name.toLowerCase().includes(term)))
            .filter((items) => items.length > 0)
            .reduce((acc, cur) => {
                return [...acc, ...cur];
            }, []);
    }, [search, categories]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value.trim());
    };

    const handleToggleList = () => setOpen((prevState) => !prevState);
    const handleOutsideListClick = () => {
        if (!open) {
            return;
        }
        
        setOpen(false)
    };
    
    const handleToggleSearch = () => setOpenSearch((prevState) => !prevState);
    const handleOutsideSearchClick = () => {
        if (!openSearch) {
            return;
        }
        
        setOpenSearch(false);
        setSearch('');
    };
    
    const handleGoToCategoryPopup = useCallback((categoryId) => () => {
        const group = restaurant?.menu_category_groups.find(g => g.categories.includes(categoryId));
        
        if (!!group && group.id !== categoryGroup) {
            setCategoryGroup(group.id);
            
            setScrollIntoView({
                selector: `#category-${categoryId}`,
            });
            
            return;
        }
        
        const element = document.querySelector(`#category-${categoryId}`);
        
        element?.scrollIntoView({
            behavior: 'smooth',
        });
        
        setOpen(false);
    }, [restaurant.menu_category_groups, categoryGroup, setCategoryGroup]);
    
    const handlerShowMenuItem = (item) => () => {
        const category = categories.find((category, index) => category.items.some((it) => it.id === item.id));

        const cartItemIds = cart.map((item) => item.id);
        const cartCategories = categories.filter((category) =>
            category.items.some((item) => cartItemIds.includes(item.id))
        );
        const cartCategoryIds = cartCategories.map((category) => category.id);
        const cartCategoryGroupWithForcedSchedule = restaurant.menu_category_groups
            .filter((group) =>
                group.categories.filter((id) => cartCategoryIds.includes(id)).length > 0
            )
            .filter((group) => group.is_use_custom_schedule)
        ;
        // const cartCategoryGroupWithForcedScheduleIds = cartCategoryGroupWithForcedSchedule.map((group) => group.id);
        const cartCategoriesWithForcedScheduleIds = cartCategories
            .filter((category) =>
                cartCategoryGroupWithForcedSchedule.some((group) =>
                    group.categories.includes(category.id)
                )
            )
            .map((category) => category.id)
        ;
        const categoryGroup = restaurant.menu_category_groups
            .find((group) =>
                group.categories.includes(category.id)
            )
        ;

        const allowOrderForCustomSchedule = !!categoryGroup && categoryGroup.is_use_custom_schedule;

        // if (isClosedRestaurant && !allowOrderForCustomSchedule) {
        //     setModalTitle('Alert');
        //     setModalMessage('Online ordering currently not available. You can try calling in your order.');
        //     setIsShowModal(true);
        //     return false;
        // }

        if (item.is_sold_out || item.is_market_price) {
            return false;
        }

        if ('show_popup' === category.display_category_schedule && category.is_special_category) {
            const currentWorkHours = getWorkHours({...restaurant, business_hours: category.business_hours}, true);
            if (!!!currentWorkHours) {
                dispatch(showCategoryDisabledAlert(category));
                return false;
            }
        }

        if (cart.length === 0) {
            if (category.is_forced_schedule) {
                dispatch(cartSetSpecialCategory(category));
            }
            else if (!category.is_forced_schedule) {
                dispatch(cartResetSpecialCategory());
            }
        }
        else if (
            (category.is_forced_schedule && !specialCategory)
            || (!category.is_forced_schedule && specialCategory)
            || (
                (category.is_forced_schedule && specialCategory)
                && category.id !== specialCategory.id
            )
        ) {
            dispatch(showCartCategoryMixAlert(specialCategory ? specialCategory : category));
            return false;
        }
        // else if (category.id !== specialCategory.id) {
        //     return false;
        // }

        if (!!categoryGroup) {
            const isHasAnotherForcedSchedule =
                categoryGroup.is_use_custom_schedule
                && cartCategories.length !== 0
                && !cartCategoriesWithForcedScheduleIds.includes(category.id);

            const isHasForcedSchedule =
                !categoryGroup.is_use_custom_schedule
                && cartCategories.length !== 0
                && cartCategoriesWithForcedScheduleIds.length !== 0;

            const hasDifferentCategoryGroup = cartCategoryIds.some((categoryId) => !categoryGroup.categories.includes(categoryId));

            if (
                hasDifferentCategoryGroup
                && (
                    isHasForcedSchedule
                    || isHasAnotherForcedSchedule
                )
            ) {
                setError(`Items from this Menu Group "${categoryGroup?.name || 'Unknown'}" cannot be mixed with items from other Menu Groups. Please update your cart and remove items if needed. You may want to make separate orders for each Menu Group.`);
                return false;
            }
        }

        dispatch(showMenuItem(item));
    };
    
    const handleResetError = () => {
        setError(null);
    };

    useClickOutside(categoriesDropdownRef, handleOutsideListClick);
    useClickOutside(searchItemsRef, handleOutsideSearchClick);
    
    useEffect(() => {
        if (!scrollIntoView) {
            return;
        }

        const timer = setTimeout(() => {
            const element = document.querySelector(scrollIntoView.selector);

            element?.scrollIntoView({
                behavior: 'smooth',
            });
            
            setOpen(false);
        }, 150);
        
        return () => {
            clearTimeout(timer);
        };
    }, [categoryGroup, scrollIntoView]);

    return (
        <div className="category-search-container">
            <Modal title="Warning!" onClose={handleResetError} isOpen={!!error}>
                <p>
                    {error}
                </p>
            </Modal>
            
            <div className="category-search-button">
                <button type="button" className="categories-button" onClick={handleToggleList}>
                    <span>
                        Categories
                    </span>
                    <span>
                        {open && (
                            <i className="fas fa-chevron-up"></i>
                        )}
                        {!open && (
                            <i className="fas fa-chevron-down"></i>
                        )}
                    </span>
                </button>
                <button type="button" className="categories-item-search-button" onClick={handleToggleSearch}>
                    <i className="fas fa-magnifying-glass"></i>
                </button>
            </div>
            <div className={classNames('categories-dropdown', { open })} ref={categoriesDropdownRef}>
                {categories.map((category) => (
                    <button
                        key={category.id}
                        type="button"
                        className="category-item-button"
                        onClick={handleGoToCategoryPopup(category.id)}
                    >
                        <span className="font-weight-bold">{category.name}</span>
                    </button>
                ))}
            </div>
            
            <FullScreenOverlay isOpen={openSearch}>
                <div className="categories-item-search-container" ref={searchItemsRef}>
                    <div className="categories-item-search-group">
                        <input type="text" className="categories-item-search-input" placeholder="Search Menu..." value={search} onChange={handleSearchChange} />
                        <div className="categories-item-search-input-icon">
                            <i className="fas fa-magnifying-glass"></i>
                        </div>
                        <button type="button" className="categories-item-search-close-button"
                                onClick={handleToggleSearch}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    
                    <div className={classNames('categories-item-search-result', { open: foundItems.length > 0 })}>
                        {foundItems.map((item) => (
                            <button key={item.id} type="button" className="categories-item-search-button" onClick={handlerShowMenuItem(item)}>
                                <span className="search-item-label">{item.name}</span>
                                <span>(${item.price.toFixed(2)})</span>
                            </button>
                        ))}
                    </div>
                </div>
            </FullScreenOverlay>
        </div>
    )
};